import { CloudUpload, DeleteOutline } from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import { ACCENT } from "../../../../../../../layout/Header/components/CreateClientModal/shared";
import { useEffect, useState } from "react";
import { getActivationAssets } from "../../../../../../../core/services/projectWorkRoom.service";

function FilesStep({ files, form, fileInputRef, onFilesSelected, onRemoveFile }) {
  const [isDragging, setIsDragging] = useState(false);
  const [fileBlobs, setFileBlobs] = useState([]); // { url, name } per file, aligned with `files` by index
  const [loadingAssets, setLoadingAssets] = useState(false);

  const deleteAssets = async () => {
    // to do: wire up bulk delete (likely one call per pre-uploaded asset + onRemoveFile for local ones)
  };

  useEffect(() => {
    let cancelled = false;
    const createdUrls = [];

    const fetchAssets = async () => {
      setLoadingAssets(true);
      try {
        const results = await Promise.all(
          files.map(async (file) => {
            // pre-uploaded file: identified by its string reference/id, needs to be fetched
            if (typeof file === "string") {
              const { data } = await getActivationAssets(form.activationId, file);
              const url = URL.createObjectURL(data);
              createdUrls.push(url);
              return { url, name: file };
            }
            // locally selected file, already a File/Blob — preview it directly
            if (file instanceof Blob) {
              const url = URL.createObjectURL(file);
              createdUrls.push(url);
              return { url, name: file.name };
            }
            return null;
          })
        );

        if (!cancelled) {
          setFileBlobs(results.filter(Boolean));
        }
      } catch (error) {
        console.error("Error fetching assets:", error);
      } finally {
        if (!cancelled) setLoadingAssets(false);
      }
    };

    fetchAssets();

    return () => {
      cancelled = true;
      createdUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [files, form.activationId]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Uploaded Assets</Typography>
        <Typography
          onClick={deleteAssets}
          sx={{ fontSize: 13, color: "rgba(255,255,255,0.5)", cursor: "pointer", "&:hover": { color: "rgba(255,255,255,0.8)" } }}
        >
          Delete all
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
          gap: 1.5,
        }}
      >
        {fileBlobs.map((asset, index) => (
          <Box key={`${asset.name}-${index}`} sx={{ position: "relative" }}>
            <Box
              sx={{
                aspectRatio: "1.3 / 1",
                borderRadius: "10px",
                overflow: "hidden",
                border: "1.5px solid rgba(255,255,255,0.1)",
                backgroundImage: `url(${asset.url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <IconButton
              size="small"
              onClick={() => onRemoveFile(index)}
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                width: 22,
                height: 22,
                background: "rgba(0,0,0,0.55)",
                color: "rgba(255,255,255,0.8)",
                "&:hover": { background: "#d32f2f", color: "#fff" },
              }}
            >
              <DeleteOutline sx={{ fontSize: 14 }} />
            </IconButton>
            <Typography
              noWrap
              sx={{ fontSize: 11, color: "rgba(255,255,255,0.6)", mt: 0.5 }}
              title={asset.name}
            >
              {asset.name}
            </Typography>
          </Box>
        ))}

        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            onFilesSelected(e.dataTransfer.files);
          }}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            aspectRatio: "1.3 / 1",
            border: `1.5px dashed ${isDragging ? ACCENT : "rgba(255,255,255,0.25)"}`,
            borderRadius: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            background: isDragging ? "rgba(219, 165, 41, 0.06)" : "rgba(255,255,255,0.02)",
            transition: "all 0.15s ease",
          }}
        >
          <CloudUpload sx={{ fontSize: 22, color: ACCENT, mb: 0.5 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: ACCENT }}>Upload</Typography>
          <Typography sx={{ fontSize: 10, color: "rgba(255,255,255,0.45)" }}>Drag or browse</Typography>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              onFilesSelected(e.target.files);
              e.target.value = "";
            }}
          />
        </Box>
      </Box>

      {!loadingAssets && files.length === 0 && (
        <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.4)", fontStyle: "italic" }}>
          No files uploaded yet.
        </Typography>
      )}
    </Stack>
  );
}

export default FilesStep;